/**
 * The color-scheme contract shared by the MUI theme factory, the document
 * shells, the pre-React script in `web/index.html`, and `web/src/index.css`.
 *
 * Mode is MUI's: `useColorScheme()` reads and writes it, MUI persists it under
 * `COLOR_MODE_STORAGE_KEY`, and the resolved scheme is stamped on `<html>` as
 * `COLOR_SCHEME_ATTRIBUTE`. Everything that must agree with MUI before React
 * mounts (the inline script, the pre-React `html` paint) reads these same
 * literals — `colorScheme.test.ts` pins `index.html` to them.
 */

/** The `<html>` attribute MUI stamps with the resolved scheme (`light` | `dark`). */
export const COLOR_SCHEME_ATTRIBUTE = 'data-mui-color-scheme'

/** localStorage key MUI persists the user's mode under (`light` | `dark` | `system`). */
export const COLOR_MODE_STORAGE_KEY = 'mui-mode'

/**
 * The mode a user who has never touched the control gets. `system` follows
 * the OS; the pre-React script in index.html carries the same default.
 */
export const DEFAULT_COLOR_MODE = 'system' as const

export type ColorMode = 'light' | 'dark' | typeof DEFAULT_COLOR_MODE

/**
 * CSS selector for "the dark scheme is active", for scheme-keyed styling that
 * must be right on first paint with no React round-trip (e.g. swapping a
 * wordmark asset). Use it as an ancestor selector: `[`${DARK_SCHEME_SELECTOR} &`]`.
 *
 * Do NOT use `theme.getColorSchemeSelector('dark')` for this: on a
 * single-scheme theme (the applicant variant) it returns `'&'`, which would
 * apply the "dark" styles unconditionally.
 */
export const DARK_SCHEME_SELECTOR = `[${COLOR_SCHEME_ATTRIBUTE}="dark"]`

/**
 * Applicant pages and navigation aliases own a light-only document outside
 * the MUI shell. Not `/apply` (ApplyLandingPage), not
 * `/apply/{code}/change-unit`, not `/apply-to`, not 404s — those are MUI
 * public routes that follow the mode. The router preserves trailing slashes,
 * so none is tolerated here (mirrors the pre-#12858 script).
 */
export const LIGHT_ONLY_PATH_PATTERN =
  /^\/(apply\/[^/]+|eve\/\d+\/applicant\/\d+(\/fake-phone)?|applications(\/\d+)?|welcome|workflow\/\d+)$/

/**
 * Stamped on <html> by the index.html script, alongside `light`, when the
 * path is one of the LIGHT_ONLY routes. The inline style in index.html and
 * index.css paint the canvas the applicant page colour (`evieTokens
 * .color.background`, also the applicant MUI variant's `background.default`) behind
 * that attribute instead of white, so a direct load of Eve/Evie starts on the
 * washi page rather than flashing white and then washi. The rule reads
 * `--mui-palette-background-default` first, so once any MUI shell mounts the
 * attribute is inert; it is never removed, and never needs to be.
 */
export const APPLICANT_CANVAS_ATTRIBUTE = 'data-applicant-canvas'
